import {HStack} from 'platform/foundation-tw';
import {cva, type VariantProps} from 'platform/shared-tw';

import type {MouseEvent, ReactNode} from 'react';

import {isNotNil} from 'ramda';

const anchorVariants = cva(
  'flex w-full cursor-pointer border-0 border-r-4 border-solid bg-transparent py-3 text-left transition-colors focus-visible:outline-2 focus-visible:outline-(--color-mira-gray-100) focus-visible:outline-offset-2',
  {
    variants: {
      state: {
        active: 'border-r-(--color-mira-gray-100)',
        idle: 'border-r-transparent hover:border-r-(--color-mira-gray-100) hover:font-semibold',
      },
    },
    defaultVariants: {
      state: 'idle',
    },
  }
);

const labelVariants = cva('text-base leading-6 font-normal', {
  variants: {
    state: {
      active: 'font-semibold text-(--color-text-primary)',
      idle: 'font-normal text-(--color-text-secondary) hover:text-(--color-text-primary)',
    },
  },
  defaultVariants: {
    state: 'idle',
  },
});

const iconContainerVariants = cva('flex h-6 items-center shrink-0', {
  variants: {
    state: {
      active: 'text-(--color-text-primary)',
      idle: 'text-(--color-text-secondary)',
    },
  },
  defaultVariants: {
    state: 'idle',
  },
});

export interface AnchorProps extends VariantProps<typeof anchorVariants> {
  readonly label: string;
  readonly icon?: ReactNode;
  readonly onClick?: () => void;
  readonly href?: string;
}

export function Anchor(props: AnchorProps) {
  const isActive = props.state === 'active';
  const hasOnClick = isNotNil(props.onClick);

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!hasOnClick) {
      return;
    }
    event.preventDefault();
    props.onClick?.();
  };

  return (
    <a
      href={isNotNil(props.href) ? `#${props.href}` : undefined}
      onClick={handleClick}
      aria-current={isActive ? 'location' : undefined}
      className={anchorVariants({state: props.state})}
    >
      <HStack spacing={4}>
        {isNotNil(props.icon) ? (
          <div className={iconContainerVariants({state: props.state})}>{props.icon}</div>
        ) : null}
        <span className={labelVariants({state: props.state})}>{props.label}</span>
      </HStack>
    </a>
  );
}
